import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import './UserDetailsPage.css';

const UserDetailsPage = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const jwt = Cookies.get("jwt");
  const decoded = jwt ? jwtDecode(jwt) : null;
  const gmail = decoded?.email;

  // Determine if this is food/restaurant template
  const isFoodTemplate = templateId === 'food-1' || templateId === 'jgrpsercstyshh';

  const [formData, setFormData] = useState({
    // Basic info
    businessName: '',
    phone: '',
    email: gmail || '',
    location: '',
    description: '',
    timings: '',
    // Restaurant specific
    logo_img: '',
    // Arrays for restaurant
    items: [],
    top_dish: [],
    special_item: {},
    discounts: [],
    testimonial: [],
  });

  const [loading, setLoading] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    item_category: '',
    item_name: '',
    item_description: '',
    item_price: '',
  });

  useEffect(() => {
    // Pre-fill email if available
    if (gmail) {
      setFormData(prev => ({ ...prev, email: gmail }));
    }
  }, [gmail]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleItemChange = (e) => {
    setCurrentItem({
      ...currentItem,
      [e.target.name]: e.target.value
    });
  };

  const addItem = () => {
    if (currentItem.item_name && currentItem.item_category) {
      setFormData({
        ...formData,
        items: [...formData.items, {
          item_category: currentItem.item_category,
          item_name: currentItem.item_name,
          item_description: currentItem.item_description || '',
          item_price: parseFloat(currentItem.item_price) || 0,
          item_discount: 0,
          item_img: '',
        }]
      });
      setCurrentItem({
        item_category: '',
        item_name: '',
        item_description: '',
        item_price: '',
      });
    }
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!jwt || !gmail) {
      alert('Please login first');
      navigate('/');
      return;
    }

    setLoading(true);
    
    try {
      const url = process.env.REACT_APP_BACKEND_URL;
      
      // Prepare data in the format expected by backend (using old User model structure)
      const userData = {
        email: gmail,
        personalInfo: {
          name: formData.businessName,
          email: gmail,
          phone: formData.phone,
          location: formData.location,
          description: formData.description,
          Timings: formData.timings,
          logo_img: formData.logo_img,
        },
        items: formData.items,
        top_dish: formData.top_dish,
        special_item: formData.special_item,
        discounts: formData.discounts,
        testimonial: formData.testimonial,
      };

      // Save to backend
      const response = await fetch(`${url}/api/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (response.ok || response.status === 200 || response.status === 201) {
        // Get user ID from response
        const userId = data.user?.id || data.id;
        
        if (userId) {
          // Redirect to generated website (restaurant template)
          navigate(`/jgrpsercstyshh/${userId}`);
        } else {
          alert('Website created! But user ID not found. Please check your websites.');
          navigate('/my-websites');
        }
      } else {
        alert('Error saving website data: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to create website. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sections = [
    {
      title: "Business Information",
      fields: [
        { name: "businessName", label: "Business Name", type: "text", required: true },
        { name: "phone", label: "Phone Number", type: "tel", required: true },
        { name: "email", label: "Email Address", type: "email", required: true, disabled: true },
        { name: "location", label: "Location/Address", type: "text", required: true },
        { name: "description", label: "Business Description", type: "textarea", rows: 4, required: true },
        { name: "timings", label: "Business Hours", type: "text", placeholder: "e.g., Mon-Sat: 10 AM - 10 PM" },
      ]
    }
  ];

  return (
    <section className="user-details-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Website Details Form</h1>
          <p className="page-subtitle">Please provide detailed information about your business</p>
        </div>
        
        <div className="user-details-content">
          <form className="user-details-form" onSubmit={handleSubmit}>
            {sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="form-section glass-card">
                <h2 className="section-title">{section.title}</h2>
                <div className="form-grid">
                  {section.fields.map((field, fieldIndex) => (
                    <div key={fieldIndex} className="form-group">
                      <label htmlFor={field.name}>{field.label}</label>
                      {field.type === 'textarea' ? (
                        <textarea
                          id={field.name}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          required={field.required}
                          rows={field.rows || 3}
                          className="form-control"
                          placeholder={field.placeholder}
                          disabled={field.disabled}
                        ></textarea>
                      ) : (
                        <input
                          type={field.type}
                          id={field.name}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          required={field.required}
                          className="form-control"
                          placeholder={field.placeholder}
                          disabled={field.disabled}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {isFoodTemplate && (
              <div className="form-section glass-card">
                <h2 className="section-title">Menu Items</h2>
                <div className="menu-item-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Category</label>
                      <input
                        type="text"
                        name="item_category"
                        value={currentItem.item_category}
                        onChange={handleItemChange}
                        placeholder="e.g., Main Course"
                      />
                    </div>
                    <div className="form-group">
                      <label>Item Name</label>
                      <input
                        type="text"
                        name="item_name"
                        value={currentItem.item_name}
                        onChange={handleItemChange}
                        placeholder="e.g., Biryani"
                      />
                    </div>
                    <div className="form-group">
                      <label>Price (₹)</label>
                      <input
                        type="number"
                        name="item_price"
                        value={currentItem.item_price}
                        onChange={handleItemChange}
                        placeholder="250"
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <input
                        type="text"
                        name="item_description"
                        value={currentItem.item_description}
                        onChange={handleItemChange}
                        placeholder="Item description"
                      />
                    </div>
                    <div className="form-group">
                      <button type="button" onClick={addItem} className="add-item-button">
                        Add Item
                      </button>
                    </div>
                  </div>

                  {formData.items.length > 0 && (
                    <div className="items-list">
                      <h3>Added Items ({formData.items.length})</h3>
                      <ul>
                        {formData.items.map((item, index) => (
                          <li key={index}>
                            <span><strong>{item.item_name}</strong> - {item.item_category} - ₹{item.item_price}</span>
                            <button type="button" onClick={() => removeItem(index)} className="remove-button">
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="form-actions">
              <button 
                type="submit" 
                className="glass-button submit-button"
                disabled={loading}
              >
                {loading ? 'Creating Website...' : 'Create Website'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default UserDetailsPage;